import React from 'react'
import styled from 'styled-components'

import pluralize from 'pluralize'

import { SearchQueryResult } from '../../../hooks/useQueries'
import { CheckboxToggle } from '../../../components/form'
import {
  searchTypes,
  disallowedFiltersForType,
} from '../../../actions/searchActions'

import { BOOKING_OPTIONS_FILTERS, BookingOptions } from './BookingOptionsFilter'
import { RoomsAndBeds } from './RoomsAndBedsFilter'
import PriceRange from './PriceRange'
import MultiSelectFilter from './MultiSelectFilter'

import { getPriceTitle, orderBySlug } from './helpers'

const FilterGroup = styled.div`
  padding: 24px 0;
  border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};
`

const Label = styled.label`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  color: ${({ theme }) => theme?.['-ds-neutral-500']?.value};
  font-weight: 300;
  line-height: 1.4;
`

const Count = styled.div`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  line-height: 1.4;
  font-weight: 400;
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  background-color: ${({ theme }) => theme?.['-ds-success-100']?.value};
  border-radius: 80px;
  display: inline-block;
  padding: 4px 10px;
  margin-left: 16px;
  position: absolute;
  top: -2px;
`

const GroupTitle = styled.h4`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-md']?.['font-size']?.value};
  position: relative;
  margin-bottom: 24px;
`

const ContentWrapper = styled.div`
  padding: 0;
`
const topSubcategoriesSlugs = [
  'tiny-house',
  'cabin',
  'cottage',
  'treehouse',
  'beach-shack',
  'farm',
] as const

const topAmenitiesSlugs = [
  'outdoor-shower-bath',
  'hot-tub',
  'indoor-fireplace',
  'bath',
  'outdoor-fireplace',
  'wifi',
] as const

type Filters = Omit<GQL_SearchFilters, 'state'> & {
  state?: string
}

type MoreFiltersProps = {
  appliedFilters: Filters
  defaults: SearchQueryResult['data']['search']['defaults']
  includeRegionsFilter?: boolean
  filterValuesState: Partial<{
    petFriendly: boolean
    regions: string[]
    priceStart: number
    priceEnd: number
    priceBounds: [number, number]
    subcategories: string[]
    amenities: string[]
    bedrooms: number
    beds: number
    fullRefund: boolean
    instantBook: boolean
    oneNightStayWeekday: boolean
    oneNightStayWeekend: boolean
  }>
  handleFiltersChange: (_: Filters) => any
  handlePriceRangeChange: (_: [number, number]) => any
}

const MoreFilters = ({
  appliedFilters,
  defaults,
  includeRegionsFilter,
  handleFiltersChange,
  handlePriceRangeChange,
  filterValuesState: {
    petFriendly,
    regions,
    priceBounds,
    priceStart,
    priceEnd,
    subcategories,
    amenities,
    bedrooms,
    beds,
    fullRefund,
    instantBook,
    oneNightStayWeekday,
    oneNightStayWeekend,
  },
}: MoreFiltersProps) => {
  const disallowedFilters = disallowedFiltersForType[appliedFilters.type]

  const currentType = searchTypes.find(
    (type) => type.slug === appliedFilters.type
  )

  const orderedSubcategories = orderBySlug(
    defaults?.subcategories,
    topSubcategoriesSlugs
  )
  const orderedAmenities = orderBySlug(defaults?.amenities, topAmenitiesSlugs)

  return (
    <>
      {disallowedFilters && !disallowedFilters.includes('petFriendly') && (
        <FilterGroup style={{ paddingTop: 0 }}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label style={{ display: 'flex', justifyContent: 'space-between' }}>
            <GroupTitle style={{ marginBottom: '8px' }}>
              Bringing pets?
            </GroupTitle>
            <CheckboxToggle
              id="pet_friendly"
              name="pet_friendly"
              aria-label="Pet friendly"
              isChecked={!!petFriendly}
              onChange={(e) =>
                handleFiltersChange({ petFriendly: e.target.checked })
              }
              style={{ margin: 0 }}
            />
          </label>
          <Label htmlFor="pet_friendly">Only show pet-friendly escapes</Label>
        </FilterGroup>
      )}

      {disallowedFilters && !disallowedFilters.includes('priceRange') && (
        <FilterGroup data-testid="price-filter-group">
          <GroupTitle>{getPriceTitle(appliedFilters)}</GroupTitle>
          <ContentWrapper>
            <PriceRange
              initialPriceStart={priceStart || priceBounds?.[0]}
              initialPriceEnd={priceEnd || priceBounds?.[1]}
              minPrice={priceBounds ? priceBounds[0] : priceStart}
              maxPrice={priceBounds ? priceBounds[1] : priceEnd}
              onChange={handlePriceRangeChange}
            />
          </ContentWrapper>
        </FilterGroup>
      )}

      {includeRegionsFilter &&
        appliedFilters.state &&
        appliedFilters.state !== 'all' && (
          <FilterGroup>
            <GroupTitle>
              Regions
              {regions?.length > 0 && <Count>{regions.length}</Count>}
            </GroupTitle>
            <ContentWrapper>
              <MultiSelectFilter
                name="regions"
                valueKey="regions"
                values={regions || []}
                items={defaults ? defaults.regions : []}
                onChange={handleFiltersChange}
              />
            </ContentWrapper>
          </FilterGroup>
        )}

      {disallowedFilters &&
        !disallowedFilters.includes('amenities') &&
        defaults && (
          <FilterGroup>
            <GroupTitle>
              Amenities
              {amenities?.length > 0 && <Count>{amenities.length}</Count>}
            </GroupTitle>
            <ContentWrapper>
              <MultiSelectFilter
                name="amenities"
                valueKey="amenities"
                values={amenities || []}
                items={orderedAmenities}
                onChange={handleFiltersChange}
              />
            </ContentWrapper>
          </FilterGroup>
        )}

      {currentType && !disallowedFilters.includes('subcategories') && defaults && (
        <FilterGroup>
          <GroupTitle>
            {`${pluralize(currentType.name, 1)} type`}
            {subcategories?.length > 0 && <Count>{subcategories.length}</Count>}
          </GroupTitle>
          <ContentWrapper>
            <MultiSelectFilter
              name="accommodation types"
              valueKey="subcategories"
              values={subcategories || []}
              items={orderedSubcategories}
              onChange={handleFiltersChange}
            />
          </ContentWrapper>
        </FilterGroup>
      )}

      {!disallowedFilters?.includes('roomsAndBeds') && (
        <FilterGroup>
          <GroupTitle>Rooms and beds</GroupTitle>
          <ContentWrapper>
            <RoomsAndBeds
              values={{ bedrooms, beds }}
              onChange={handleFiltersChange}
            />
          </ContentWrapper>
        </FilterGroup>
      )}

      {!BOOKING_OPTIONS_FILTERS.every((filter) =>
        disallowedFilters?.includes(filter)
      ) && (
        <FilterGroup>
          <GroupTitle>Booking options</GroupTitle>
          <ContentWrapper>
            <BookingOptions
              showOneNightStayOptions={
                !appliedFilters.startDate && !appliedFilters.endDate
              }
              values={{
                fullRefund,
                instantBook,
                oneNightStayWeekday,
                oneNightStayWeekend,
              }}
              onChange={handleFiltersChange}
            />
          </ContentWrapper>
        </FilterGroup>
      )}
    </>
  )
}

export default MoreFilters
